import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { ScenarioExt } from '@/classes/scenario/ScenarioExt';
import { ScenarioPVExt } from '@/classes/scenario/pv/ScenarioPVExt';
import { PVCurve } from '@/dto/scenario/pv/PVCurve';
import { PVCurveExt } from '@/classes/scenario/pv/PVCurveExt';
import { ScenarioPayloads } from '@/store/modules/scenario';
import { DispatchOptions, Payload } from 'vuex';
import { FieldStatus } from '@/classes/FieldStatus';

interface ReferencesService {
  getPVRef: () => Promise<Record<string, unknown>>;
}

interface Dependencies {
  referencesService: ReferencesService;
  post: <T>(url: string, body: unknown, cancelKey: string) => Promise<{data: T}>;
  errorHandler: (error: unknown) => void;
}

interface CalculatedCurvePayload {
  values: number[];
  durationYear: string;
  startDate: Date;
  stepRef: string;
}

class InvalidateCurve implements Payload {
  readonly type = 'pv/invalidateCurve';

  public static buildPayload(): [InvalidateCurve, DispatchOptions] {
    return [new InvalidateCurve(), { root: true }];
  }
}

class LoadPVCurve implements Payload {
  public readonly type = 'pv/loadPVCurve';
  public readonly scenario: ScenarioPVExt;

  constructor(options: Omit<LoadPVCurve, 'type'>) {
    this.scenario = options.scenario;
  }

  public static buildPayload(options: Omit<LoadPVCurve, 'type'>): [LoadPVCurve, DispatchOptions] {
    return [new LoadPVCurve(options), { root: true }];
  }
}

export const PVPayloads = {
  InvalidateCurve,
  LoadPVCurve
};

export const buildPVModule = ({ referencesService, post, errorHandler }: Dependencies) => {
  @Module({
    namespaced: true
  })
  class PVModule extends VuexModule {
    private pvFileMap: Map<string, File> = new Map();
    private presets: Record<string, unknown> | null = null;

    @Action({ commit: 'setPvFileField' })
    public setPvFile(payload: File | null): { id: string; data: File | null } {
      return { id: this.scenarioId, data: payload };
    }

    @Action
    public async invalidateCurve() {
      return this.context.dispatch(...ScenarioPayloads.SaveFields.buildPayload({
        scenario: new ScenarioExt({
          ...this.getConfig,
          pv: new ScenarioPVExt({
            ...this.getConfig.pv,
            calculatedCurve: null
          })
        })
      }));
    }

    /**
     *
     * @param payload : pv used to compute the pv curves
     *
     * At the moment we don't need to call the generate api since
     * we already have pv->file->values. It may be useful IF we
     * have to set a start date, hour etc as we do in load/site.
     */
    @Action
    public async loadPVCurve(payload: LoadPVCurve) {
      if (this.context.getters.pVCurveErrors.length !== 0) {
        return;
      }
      try {
        const { data } = await post<{ calculatedCurve: CalculatedCurvePayload }>(`/pv/generate`, payload.scenario.toDto(), 'loadPVCurve');
        const resultDTO = new PVCurve({
          values: data.calculatedCurve.values,
          durationYear: data.calculatedCurve.durationYear,
          startDate: data.calculatedCurve.startDate,
          stepMinutes: data.calculatedCurve.stepRef // TODO this mapping would not be necessary if backend returns a PVCurve
        });
        const currentScenario = this.context.getters.getConfig;
        const updatedScenario = new ScenarioExt({
          ...currentScenario,
          pv: new ScenarioPVExt({
            ...payload.scenario,
            calculatedCurve: PVCurveExt.fromDto(resultDTO)
          })
        });
        await this.context.dispatch(...ScenarioPayloads.SaveFields.buildPayload({ scenario: updatedScenario }));
      } catch (error) {
        errorHandler(error);
      }
    }

    @Action
    public async loadPresets(): Promise<typeof PVModule.prototype.presets> {
      if (this.presets !== null) {
        return this.presets;
      }
      try {
        const presets = await referencesService.getPVRef();
        if (presets) {
          this.context.commit('setPresets', presets);
        }
      } catch (error) {
        errorHandler(error);
      }
      return this.presets;
    }

    @Mutation
    private setPresets(payload: typeof PVModule.prototype.presets): void {
      this.presets = payload;
    }

    @Mutation
    private setPvFileField(payload: { id: string; data: File }): void {
      this.pvFileMap.set(payload.id, payload.data);
      this.pvFileMap = new Map(this.pvFileMap);
    }

    public get getConfig(): ScenarioExt {
      return new ScenarioExt(this.context.rootGetters['scenario/getFields']);
    }

    public get scenarioId(): string {
      return this.getConfig.uuid || '';
    }

    public get getFields(): ScenarioPVExt {
      return new ScenarioPVExt(this.getConfig.pv);
    }

    public get getFile(): File | null {
      return this.pvFileMap.get(this.scenarioId) || null;
    }

    public get getPresets(): typeof PVModule.prototype.presets {
      return this.presets;
    }

    public get pVCurveErrors(): FieldStatus[] {
      const errors: FieldStatus[] = this.context.rootGetters['scenario/getFieldErrors'];
      return errors.filter(error => error.isUsedForPvCurve());
    }
  }

  return PVModule;
};
